import { useRef, useState, type ReactNode } from 'react';
import {
  ResultsFilterChips,
  ResultsSortSheet,
  type ResultsFilterChip,
} from '../../shared/results_page/results_filter_chips.js';
import type { BusResultsFiltersCopy } from './bus_results_filters.js';
import { BusResultsFiltersSheet } from './bus_results_filters_sheet.js';
import {
  activeBusFilterCount,
  BUS_RESULTS_ORDERS,
  DEFAULT_BUS_RESULTS_ORDER,
  type BusFilterableOffer,
  type BusResultsFilterValue,
  type BusResultsOrder,
} from './bus_results_rules.js';
import type { BusResultsSortCopy } from './bus_results_sort.js';

/** A copy que só os chips precisam — o resto vem do painel e do ordenador. */
export type BusResultsChipsCopy = {
  /** O nome acessível da fila ("Filtros e ordenação"). */
  toolbarLabel: string;
  /** O ← das folhas dos chips ("Voltar"). */
  backLabel: string;
  /** O chip da política, mais curto que o rótulo do painel ("Dentro da
   *  política", como o do hotel e o do carro). */
  policyLabel: string;
};

type OpenSheet = 'filters' | 'sort' | null;

/**
 * A fila de chips do ÔNIBUS no palco estreito — a mesma anatomia da do carro
 * (`RentalResultsFilterChips`): `Filtros` com o contador (o painel completo na
 * folha), a ordenação (a folha de ordenar) e o liga/desliga da política, que
 * vira na hora. Os outros eixos (classe, horários, preço, viação) ficam no
 * painel: nenhum deles é um toque só.
 */
export function BusResultsFilterChips({
  offers,
  currency,
  filters,
  order,
  filtersCopy,
  sortCopy,
  copy,
  applyLabel,
  locked,
  onQueryChange,
}: {
  readonly offers: readonly BusFilterableOffer[];
  readonly currency: string;
  readonly filters: BusResultsFilterValue;
  readonly order: BusResultsOrder;
  readonly filtersCopy: BusResultsFiltersCopy;
  readonly sortCopy: BusResultsSortCopy;
  readonly copy: BusResultsChipsCopy;
  /** O botão do pé da folha de filtros ("Aplicar filtros"). */
  readonly applyLabel: string;
  readonly locked: boolean;
  readonly onQueryChange: (query: {
    filters: BusResultsFilterValue;
    order: BusResultsOrder;
  }) => void;
}): ReactNode {
  const [sheet, setSheet] = useState<OpenSheet>(null);
  const chipRefs = useRef(new Map<string, HTMLButtonElement>());
  /** O chip que abriu a folha em cena — o alvo do foco de volta. */
  const openerRef = useRef<HTMLElement | null>(null);
  const applyFilters = (next: BusResultsFilterValue) => {
    onQueryChange({ filters: next, order });
  };
  const openSheet = (next: Exclude<OpenSheet, null>) => () => {
    openerRef.current = chipRefs.current.get(next) ?? null;
    setSheet(next);
  };
  const onOpenChange = (open: boolean) => {
    if (!open) setSheet(null);
  };
  const count = activeBusFilterCount(filters);
  const chips: ResultsFilterChip[] = [
    {
      id: 'filters',
      kind: 'filters',
      label: filtersCopy.filtersLabel,
      active: count > 0,
      badge: count,
      onPress: openSheet('filters'),
    },
    {
      id: 'sort',
      kind: 'sort',
      label: sortCopy.orderLabel(order),
      active: order !== DEFAULT_BUS_RESULTS_ORDER,
      onPress: openSheet('sort'),
    },
    {
      id: 'policy',
      kind: 'toggle',
      label: copy.policyLabel,
      active: filters.policyOnly,
      onPress: () => {
        applyFilters({ ...filters, policyOnly: !filters.policyOnly });
      },
    },
  ];
  return (
    <>
      <ResultsFilterChips
        chips={chips}
        label={copy.toolbarLabel}
        disabled={locked}
        chipRefs={chipRefs}
      />
      <BusResultsFiltersSheet
        open={sheet === 'filters'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        offers={offers}
        currency={currency}
        value={filters}
        copy={filtersCopy}
        applyLabel={applyLabel}
        locked={locked}
        onApply={applyFilters}
      />
      <ResultsSortSheet
        open={sheet === 'sort'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        title={sortCopy.sortLabel}
        backLabel={copy.backLabel}
        orders={BUS_RESULTS_ORDERS}
        value={order}
        orderLabel={(candidate) => sortCopy.orderLabel(candidate)}
        locked={locked}
        onChange={(next) => {
          onQueryChange({ filters, order: next });
        }}
      />
    </>
  );
}
